import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Avatar,
  Box,
  IconButton,
  TextField,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import {
  PhoneAndroid,
  Computer,
  PhoneIphone,
  Devices,
  Language,
  Extension,
  LaptopMac,
  Edit,
  Check,
  Close,
  Delete,
} from '@mui/icons-material';
import { Device } from '../services/api';
import { trackButtonClick, trackDeviceAction } from '../utils/analytics';

interface DeviceCardProps {
  device: Device;
  isSelected: boolean;
  onClick: () => void;
  onRename?: (deviceId: string, newName: string) => void;
  onDelete?: (deviceId: string) => void;
  isCurrentDevice?: boolean;
  hasNewMessages?: boolean;
}

const DeviceCard: React.FC<DeviceCardProps> = ({ device, isSelected, onClick, onRename, onDelete, isCurrentDevice = false, hasNewMessages = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(device.name);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);



  const getDeviceIcon = (deviceType: string) => {
    switch (deviceType.toLowerCase()) {
      case 'android':
        return <PhoneAndroid />;
      case 'ios':
        return <PhoneIphone />;
      case 'apple':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"/>
          </svg>
        );
      case 'web':
        return <Language />; // Globe icon for web browser
      case 'chrome':
        return <Extension />; // Extension icon for Chrome extension
      default:
        return <Devices />;
    }
  };

  const formatLastSeen = (lastSeenAt?: string) => {
    if (!lastSeenAt) return 'Never';
    
    const date = new Date(lastSeenAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return `${Math.floor(diffMins / 1440)}d ago`;
  };

  const handleRename = () => {
    if (onRename && editName.trim() && editName !== device.name) {
      trackButtonClick('Save Rename', 'Device Card');
      trackDeviceAction('rename_save', device.deviceType);
      onRename(device.id, editName.trim());
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    trackButtonClick('Cancel Rename', 'Device Card');
    setEditName(device.name);
    setIsEditing(false);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    trackButtonClick('Delete Device', 'Device Card');
    trackDeviceAction('delete_initiate', device.deviceType);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = () => {
    trackButtonClick('Confirm Delete Device', 'Device Card Dialog');
    trackDeviceAction('delete_confirm', device.deviceType);
    if (onDelete) {
      onDelete(device.id);
    }
    setShowDeleteDialog(false);
  };

  return (
    <>
      <Card
        sx={{
          cursor: 'pointer',
          border: isSelected ? 2 : 1,
          borderColor: isSelected ? '#2196F3' : 'rgba(255, 255, 255, 0.2)',
          backgroundColor: isSelected ? 'rgba(33, 150, 243, 0.2)' : 'rgba(255, 255, 255, 0.12)',
          backdropFilter: 'blur(10px)',
          color: '#FFFFFF',
          '&:hover': {
            borderColor: '#2196F3',
            backgroundColor: 'rgba(255, 255, 255, 0.18)',
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          },
          // Green background animation for new messages
          animation: hasNewMessages ? 'fadeToWhite 3s ease-out forwards' : 'none',
          '@keyframes fadeToWhite': {
            '0%': {
              backgroundColor: 'rgba(76, 175, 80, 0.3)',
            },
            '100%': {
              backgroundColor: 'rgba(255, 255, 255, 0.12)',
            },
          },
        }}
        onClick={onClick}
      >
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            {getDeviceIcon(device.deviceType)}
          </Avatar>
          
          <Box sx={{ flexGrow: 1 }}>
            {isEditing ? (
              <TextField
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') handleRename();
                  if (e.key === 'Escape') handleCancel();
                }}
                size="small"
                autoFocus
                fullWidth
                variant="standard"
              />
            ) : (
              <Typography variant="subtitle1" fontWeight="medium" sx={{ color: '#FFFFFF' }}>
                {device.name}
              </Typography>
            )}
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              {device.deviceType} • {formatLastSeen(device.lastSeenAt)}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Show delete button for non-current devices */}
            {!isCurrentDevice && onDelete && (
              <Tooltip title="Delete device">
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: '#F44336',
                    '&:hover': {
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(e);
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {/* Show rename button only for current device */}
            {isCurrentDevice && onRename && (
              <>
                {isEditing ? (
                  <>
                    <Tooltip title="Save">
                      <IconButton size="small" onClick={handleRename}>
                        <Check fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <IconButton size="small" onClick={handleCancel}>
                        <Close fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : (
                    <Tooltip title="Rename device">
                    <IconButton 
                      size="small" 
                      sx={{ color: '#FFFFFF' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        trackButtonClick('Edit Device', 'Device Card');
                        trackDeviceAction('edit_initiate', device.deviceType);
                        setIsEditing(true);
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
          </Box>
        </Box>

        {/* Delete button for current device */}
        {isCurrentDevice && onDelete && (
          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
            <Button
              variant="contained"
              sx={{ 
                backgroundColor: '#F44336',
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: '#D32F2F',
                }
              }}
              startIcon={<Delete />}
              fullWidth
              size="small"
              onClick={handleDeleteClick}
            >
              Delete
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>

      {/* Delete confirmation dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
      >
        <DialogTitle>
          {isCurrentDevice ? 'Delete Current Device?' : 'Delete Device?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {isCurrentDevice ? (
              <>
                <strong>This will:</strong>
                <br />• Remove this device from the list
                <br />• Clear all local cached data
                <br />• Clear all downloaded images
                <br />• Sign you out of the app
                <br /><br />
                <strong style={{ color: 'red' }}>This action cannot be undone.</strong>
              </>
            ) : (
              <>
                <strong>This will:</strong>
                <br />• Remove this device from the list
                <br />• Delete all messages and data for this device
                <br /><br />
                <strong style={{ color: 'red' }}>This action cannot be undone.</strong>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              trackButtonClick('Cancel Delete Device', 'Device Card Dialog');
              setShowDeleteDialog(false);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            {isCurrentDevice ? 'Delete & Sign Out' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DeviceCard;
